import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TARQ, BBQ, IMAGES } from "@/lib/brand";
import { useSEO } from "@/lib/useSEO";

/**
 * Two Tables · One Coast — cinematic split landing.
 * Two contrasting universes (sea ↔ fire), parallax, particles, custom cursor.
 */
export default function Gateway() {
  const [hovered, setHovered] = useState(null); // "tarq" | "bbq" | null
  const [exiting, setExiting] = useState(null); // null | "tarq" | "bbq"
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const [cursorVisible, setCursorVisible] = useState(false);
  const cursorRef = useRef(null);
  const rafRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Two Tables · Costa da Caparica";
    // SEO meta
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Dois espaços únicos na Costa da Caparica. Tarquínio Beach Club ou BBQ Assador — escolha a sua experiência.";
  }, []);

  useSEO({
    title: "Tarquínio × BBQ Assador — Costa da Caparica",
    description:
      "Duas casas, dois mundos. Tarquínio Beach Club à beira-mar e BBQ Assador, casa de petiscos. Reservas via WhatsApp 917 240 397.",
    image: IMAGES.tarqHero,
    imageAlt: "Tarquínio Beach Club & BBQ Assador — Costa da Caparica",
    type: "website",
    themeColor: "#1B2B3A",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Restaurant",
          "@id": "https://tarquinio-bbq.com/#tarquinio",
          name: "Tarquínio Beach Club",
          description:
            "Beach Club, Bar e Cafetaria à beira-mar na Costa da Caparica.",
          servesCuisine: ["Portuguesa", "Mariscos", "Peixe fresco", "Cocktails"],
          priceRange: "€€",
          telephone: "+351917240397",
          image: TARQ.logo,
          address: {
            "@type": "PostalAddress",
            streetAddress: "Muralha da Praia, Apoio 14",
            postalCode: "2825-382",
            addressLocality: "Costa da Caparica",
            addressCountry: "PT",
          },
          openingHours: "Mo-Su 09:00-23:00",
          sameAs: [TARQ.instagram, TARQ.facebook],
          url: "https://tarquinio-bbq.com/tarquinio",
        },
        {
          "@type": "Restaurant",
          "@id": "https://tarquinio-bbq.com/#bbq",
          name: "BBQ Assador — Casa de Petiscos",
          description:
            "Grelha portuguesa, peixe fresco, carnes selecionadas e petiscos tradicionais portugueses.",
          servesCuisine: ["Portuguesa", "Grelhados", "Petiscos", "Carnes"],
          priceRange: "€€",
          telephone: "+351917240397",
          image: BBQ.logo,
          address: {
            "@type": "PostalAddress",
            streetAddress: "Av. Gen. Humberto Delgado 31 D",
            postalCode: "2825-343",
            addressLocality: "Costa da Caparica",
            addressCountry: "PT",
          },
          openingHours: "Mo-Su 10:00-22:00",
          url: "https://tarquinio-bbq.com/bbq",
        },
      ],
    },
  });

  /* ---------- Mouse parallax + custom cursor ---------- */
  const onMouseMove = (e) => {
    const x = e.clientX / window.innerWidth;
    const y = e.clientY / window.innerHeight;
    setMouse({ x, y });
    if (cursorRef.current) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        if (cursorRef.current) {
          cursorRef.current.style.transform = `translate3d(${e.clientX - 14}px, ${e.clientY - 14}px, 0)`;
        }
      });
    }
  };

  /* ---------- Click: animated exit before navigate ---------- */
  const handleEnter = (side) => (e) => {
    e.preventDefault();
    setExiting(side);
    setTimeout(() => navigate(side === "tarq" ? "/tarquinio" : "/bbq"), 480);
  };

  return (
    <main
      data-testid="gateway-root"
      className={`relative h-[100svh] w-full overflow-hidden bg-[#000] font-gateway-sans select-none transition-opacity duration-500 ${
        exiting ? "opacity-0" : "opacity-100"
      }`}
      onMouseMove={onMouseMove}
      onMouseEnter={() => setCursorVisible(true)}
      onMouseLeave={() => setCursorVisible(false)}
      style={{ cursor: "none" }}
    >
      {/* ============ INITIAL DARK CURTAIN (fades behind the split panes) ============ */}
      <div className="absolute inset-0 bg-[#000] pointer-events-none" />

      {/* ============ TWO PANES (split slide-in) ============ */}
      <div className="absolute inset-0 flex flex-col md:flex-row">
        <Pane
          side="tarq"
          theme="light"
          bgImage={IMAGES.tarqHero}
          bgFallback="#1B2B3A"
          mouse={mouse}
          parallaxDir={1}
          active={hovered === "tarq"}
          dimmed={hovered === "bbq"}
          slideClass="tarq-slide-in"
        />
        <Pane
          side="bbq"
          theme="dark"
          bgImage={IMAGES.bbqExterior}
          bgFallback="#0A0A0A"
          mouse={mouse}
          parallaxDir={-1}
          active={hovered === "bbq"}
          dimmed={hovered === "tarq"}
          slideClass="bbq-slide-in"
        />
      </div>

      {/* ============ HEADER (top eyebrow) ============ */}
      <header className="absolute top-0 left-0 right-0 z-40 pt-7 sm:pt-9 px-5 sm:px-10 pointer-events-none">
        <div className="text-center">
          <span
            className="gateway-fade inline-block text-[10px] sm:text-[11px] tracking-[0.42em] uppercase text-white/55 font-gateway-sans drop-shadow"
            style={{ animationDelay: "1100ms" }}
          >
            Costa da Caparica — Portugal
          </span>
        </div>
      </header>

      {/* ============ TWO PANES — content layer (z-20) ============ */}
      <section className="relative z-20 flex flex-col md:flex-row h-full md:absolute md:inset-0 pointer-events-none">
        <ChoiceContent
          side="tarq"
          onEnter={handleEnter("tarq")}
          onHoverIn={() => setHovered("tarq")}
          onHoverOut={() => setHovered(null)}
          eyebrow="Beach Club · Bar · Cafetaria"
          eyebrowPos="top-left"
          name="Tarquínio"
          italic="Beach Club."
          tagline="Leve. Natural. À beira-mar."
          ctaLabel="Entrar no Beach Club"
          theme="light"
          logo={TARQ.logo}
          slideClass="tarq-slide-in"
          stagger={900}
        />
        <ChoiceContent
          side="bbq"
          onEnter={handleEnter("bbq")}
          onHoverIn={() => setHovered("bbq")}
          onHoverOut={() => setHovered(null)}
          eyebrow="Casa de Petiscos · Costa da Caparica"
          eyebrowPos="top-right"
          name="BBQ"
          italic="Assador."
          tagline="Fogo. Sabor. Intensidade."
          ctaLabel="Entrar no Assador"
          theme="dark"
          logo={BBQ.logo}
          slideClass="bbq-slide-in"
          stagger={1050}
        />
      </section>

      {/* ============ CENTRAL DIVIDER + CARD ============ */}
      <CentralDivider />

      {/* ============ CUSTOM CURSOR (desktop) ============ */}
      <div
        ref={cursorRef}
        aria-hidden
        className={`hidden md:block fixed top-0 left-0 z-[200] pointer-events-none transition-opacity duration-200 mix-blend-difference ${
          cursorVisible ? "opacity-100" : "opacity-0"
        }`}
        style={{ willChange: "transform" }}
      >
        <div
          className={`h-7 w-7 rounded-full border-2 transition-colors duration-300 ${
            hovered === "bbq"
              ? "border-[#E8521A]"
              : "border-[#D4734A]"
          }`}
        />
      </div>
    </main>
  );
}

/* ============ Pane — bg + overlay + parallax + particles ============ */
const Pane = ({ side, theme, bgImage, mouse, parallaxDir, active, dimmed, slideClass }) => {
  const isLight = theme === "light";

  // Parallax: shift bg up to ±10px based on mouse position
  const xOff = (mouse.x - 0.5) * 20 * parallaxDir;
  const yOff = (mouse.y - 0.5) * 20 * parallaxDir;

  return (
    <div
      className={`relative flex-1 min-h-[50svh] md:min-h-0 overflow-hidden ${slideClass}`}
      data-testid={`gateway-pane-${side}`}
    >
      {/* BG image with parallax */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-300 ease-out"
        style={{
          backgroundImage: `url(${bgImage})`,
          transform: `translate3d(${xOff}px, ${yOff}px, 0) scale(1.06)`,
        }}
      />

      {/* Gradient overlay */}
      {isLight ? (
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(27,43,58,0.78)_0%,rgba(27,43,58,0.42)_60%,rgba(212,115,74,0.22)_100%)]" />
      ) : (
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(10,10,10,0.86)_0%,rgba(10,10,10,0.52)_60%,rgba(232,82,26,0.26)_100%)]" />
      )}

      {/* Subtle grain texture */}
      <div className="absolute inset-0 opacity-30 mix-blend-overlay">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <filter id={`gw-noise-${side}`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" />
            <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.6 0" />
          </filter>
          <rect width="100%" height="100%" filter={`url(#gw-noise-${side})`} />
        </svg>
      </div>

      {/* Hover dim/active layer */}
      <div
        className={`absolute inset-0 transition-all duration-700 ease-out ${
          active ? "bg-transparent" : dimmed ? "bg-black/30 backdrop-blur-[2px]" : "bg-transparent"
        }`}
      />

      {/* Particles loop */}
      <Particles theme={theme} />
    </div>
  );
};

/* ============ Particles — sand for tarq / sparks for bbq ============ */
const Particles = ({ theme }) => {
  const isLight = theme === "light";
  const seeds = [...Array(14)].map((_, i) => ({
    left: `${(i * 7.3 + 5) % 95}%`,
    delay: `${(i * 0.43) % 5}s`,
    duration: `${4 + ((i * 0.7) % 3)}s`,
    size: 2 + ((i * 0.6) % 3),
  }));
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {seeds.map((s, i) => (
        <span
          key={i}
          className={`absolute bottom-0 rounded-full ${
            isLight ? "gw-particle-sand" : "gw-particle-spark"
          }`}
          style={{
            left: s.left,
            width: `${s.size}px`,
            height: `${s.size}px`,
            animationDelay: s.delay,
            animationDuration: s.duration,
            background: isLight ? "rgba(245,239,224,0.85)" : "rgba(232,82,26,0.95)",
            boxShadow: isLight
              ? "0 0 6px rgba(245,239,224,0.6)"
              : "0 0 8px rgba(232,82,26,0.7)",
          }}
        />
      ))}
    </div>
  );
};

/* ============ ChoiceContent — text + CTA per side ============ */
const ChoiceContent = ({
  side,
  onEnter,
  onHoverIn,
  onHoverOut,
  eyebrow,
  eyebrowPos,
  name,
  italic,
  tagline,
  ctaLabel,
  theme,
  logo,
  slideClass,
  stagger,
}) => {
  const isLight = theme === "light";
  const accent = isLight ? "#D4734A" : "#E8521A";

  return (
    <button
      type="button"
      onClick={onEnter}
      onMouseEnter={onHoverIn}
      onMouseLeave={onHoverOut}
      onFocus={onHoverIn}
      onBlur={onHoverOut}
      data-testid={`gateway-enter-${side === "tarq" ? "tarquinio" : "bbq"}`}
      aria-label={`Entrar em ${name} ${italic}`}
      className={`group relative flex-1 flex items-end md:items-center min-h-[55svh] md:min-h-0 outline-none focus-visible:outline-2 focus-visible:outline-white pointer-events-auto text-left`}
      style={{ cursor: "none" }}
    >
      {/* Top eyebrow */}
      <span
        className={`absolute z-10 text-[9px] sm:text-[10px] tracking-[0.32em] uppercase text-white/65 font-gateway-sans drop-shadow ${
          eyebrowPos === "top-right"
            ? "top-7 sm:top-10 right-7 sm:right-10 text-right"
            : "top-7 sm:top-10 left-7 sm:left-10"
        } gateway-fade`}
        style={{ animationDelay: `${stagger - 200}ms` }}
      >
        {eyebrow}
      </span>

      {/* Bottom-corner brand mark — typographic for BBQ, image for Tarq */}
      {side === "bbq" ? (
        <div
          aria-hidden
          className="absolute z-10 bottom-7 sm:bottom-10 right-7 sm:right-10 flex flex-col items-end leading-none opacity-70 group-hover:opacity-100 transition-opacity duration-500 gateway-fade"
          style={{ animationDelay: `${stagger + 200}ms` }}
        >
          <span className="font-gateway-serif text-white text-3xl sm:text-4xl tracking-tight leading-none">
            BBQ
          </span>
          <span className="font-gateway-sans text-[#E8521A] text-[8px] sm:text-[9px] tracking-[0.42em] uppercase leading-none mt-1">
            Assador
          </span>
        </div>
      ) : (
        <img
          src={logo}
          alt=""
          aria-hidden
          className="absolute z-10 w-12 sm:w-14 opacity-60 group-hover:opacity-90 transition-opacity duration-500 gateway-fade bottom-7 sm:bottom-10 left-7 sm:left-10"
          style={{ animationDelay: `${stagger + 200}ms` }}
        />
      )}

      {/* Content block */}
      <div
        className={`relative z-10 w-full px-7 sm:px-12 lg:px-20 pb-24 md:pb-0 ${
          side === "bbq" ? "md:text-right md:items-end" : ""
        }`}
      >
        <div
          className={`max-w-md ${side === "bbq" ? "md:ml-auto" : ""}`}
        >
          {/* Title */}
          <h2
            className="gateway-fade font-gateway-serif text-white font-bold leading-[0.85] tracking-[-0.02em] transition-transform duration-700 group-hover:scale-[1.015]"
            style={{
              fontSize: "clamp(4rem, 7vw, 7rem)",
              animationDelay: `${stagger}ms`,
            }}
          >
            {name}
            <br />
            <em
              className="italic font-normal"
              style={{ color: accent }}
            >
              {italic}
            </em>
          </h2>

          {/* Animated underline (hover-reveal) */}
          <div
            className="mt-3 h-[2px] w-0 group-hover:w-16 transition-all duration-700 ease-out"
            style={{ background: accent }}
          />

          {/* Tagline */}
          <p
            className="gateway-fade mt-4 text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-white/72 font-gateway-sans"
            style={{ animationDelay: `${stagger + 100}ms` }}
          >
            {tagline}
          </p>

          {/* CTA */}
          <div
            className="gateway-fade mt-8"
            style={{ animationDelay: `${stagger + 200}ms` }}
          >
            {isLight ? (
              <span className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full border-2 border-white text-white text-[11px] tracking-[0.18em] uppercase font-gateway-sans transition-all duration-300 group-hover:bg-white group-hover:text-[#1B2B3A] group-hover:translate-x-1">
                {ctaLabel}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.75} />
              </span>
            ) : (
              <span
                className="bbq-cta-pill inline-flex items-center gap-3 px-7 py-3.5 rounded-full border-2 text-[11px] tracking-[0.18em] uppercase font-gateway-sans transition-all duration-300 group-hover:translate-x-1"
              >
                {ctaLabel}
                <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

/* ============ Central divider with floating card ============ */
const CentralDivider = () => (
  <>
    {/* Vertical pulse line — desktop only */}
    <div
      className="hidden md:block absolute top-[20%] bottom-[20%] left-1/2 -translate-x-1/2 z-30 pointer-events-none"
      aria-hidden
    >
      <div
        className="h-full w-[2px] gw-divider-pulse"
        style={{
          background:
            "linear-gradient(to bottom, transparent 0%, #D4734A 30%, #E8521A 70%, transparent 100%)",
        }}
      />
    </div>

    {/* Floating central card */}
    <div
      className="absolute z-30 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 md:-translate-y-1/2 px-5 sm:px-0 w-[88%] sm:w-auto pointer-events-none"
      data-testid="gateway-center-card"
    >
      <div
        className="gw-float gateway-fade relative px-7 sm:px-9 py-6 sm:py-7 rounded-2xl text-center"
        style={{
          background: "rgba(245, 239, 224, 0.95)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: "0 24px 70px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.5) inset",
          animationDelay: "1300ms",
        }}
      >
        <span className="block text-[9px] sm:text-[10px] tracking-[0.32em] uppercase font-gateway-sans text-[#1B2B3A]/55">
          Escolha a sua experiência
        </span>
        <div
          className="mx-auto my-2.5 w-12 h-[1px]"
          style={{ background: "linear-gradient(to right, #D4734A, #E8521A)" }}
        />
        <h1 className="font-gateway-serif italic text-[#1B2B3A] text-[1.5rem] sm:text-[2rem] leading-[1.1] tracking-tight">
          Duas casas.
          <br />
          <span className="gw-gradient-text">Dois mundos.</span>
        </h1>
      </div>
    </div>
  </>
);
